import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera } from '@fortawesome/free-solid-svg-icons';
import { ImageRepositoryWidgetInstanceConfig, ImageRepositoryDataContent, RepositoryImage } from '../../../../../types';
import { useToast } from '../../../../../hooks/useToast';
import { uploadWidgetFile, downloadWidgetFile, deleteWidgetFile } from '../../../../../services/storageService';
import ConfirmationModal from '../../../../common/ConfirmationModal';
import Button from '../../../../ui/Button';
import SearchBar from '../../../../ui/SearchBar';
import PopoutWindow from '../../../../ui/PopoutWindow';
import DropZone from '../../../../ui/DropZone';
import { WidgetEmptyState } from '../../../design';
import { useRepositoryItems, RepositoryModal, useWidgetPrefs } from '../../../blocks/repository';
import { getWidgetIcon } from '../../../registry';

// Image with loading skeleton
const LoadingImg: React.FC<{ src: string; alt: string; className?: string }> = ({ src, alt, className }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <>
      {!loaded && <div className="w-full h-full bg-neutral-200 animate-pulse rounded" />}
      <img src={src} alt={alt} className={`${className} ${loaded ? '' : 'hidden'}`} onLoad={() => setLoaded(true)} />
    </>
  );
};

interface ImageRepositoryWidgetRendererProps {
  config: ImageRepositoryWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: ImageRepositoryDataContent) => void;
  readOnly?: boolean;
  assignmentId?: string;
}

const ImageRepositoryWidgetRenderer: React.FC<ImageRepositoryWidgetRendererProps> = ({
  config,
  dashboardData = {},
  onDataChange,
  readOnly = false,
  assignmentId,
}) => {
  const toast = useToast();

  // Repository CRUD, search, and copy-ref via shared hook
  const {
    items: images, searchQuery, setSearchQuery,
    filteredItems: filteredImages, copiedId, handleCopyRef,
    createItem, updateItem, deleteItem,
    deletingItemId: deletingImageId, setDeletingItemId: setDeletingImageId,
    confirmDelete,
  } = useRepositoryItems<RepositoryImage>({
    config,
    dashboardData,
    onDataChange: onDataChange as ((dataKey: string, data: unknown) => void) | undefined,
    itemsKey: 'images',
    idPrefix: 'img',
    searchFields: ['title', 'description', 'tags'],
  });

  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const { showList, setShowList, sortBy, setSortBy } = useWidgetPrefs(config.dataKey);

  // Sort filtered items — pinned always first
  const sortedImages = useMemo(() => {
    return [...filteredImages].sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      switch (sortBy) {
        case 'title': return a.title.localeCompare(b.title);
        case 'created': return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'updated': return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }
    });
  }, [filteredImages, sortBy]);
  const [viewMode, setViewMode] = useState<'grid' | 'inline'>('grid');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingImage, setEditingImage] = useState<RepositoryImage | null>(null);
  const [newImageTitle, setNewImageTitle] = useState('');
  const [newImageDescription, setNewImageDescription] = useState('');
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [uploadMimeType, setUploadMimeType] = useState<string>('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cache of blob URLs for storage-type images
  const [storageUrls, setStorageUrls] = useState<Record<string, string>>({});

  // Load blob URLs for storage-type images
  const useStorageApi = Boolean(assignmentId);
  const loadStorageImage = useCallback(async (image: RepositoryImage) => {
    if (image.imageType !== 'storage' || !image.storageFileId || !assignmentId) return;
    if (storageUrls[image.id]) return;
    try {
      const blobUrl = await downloadWidgetFile(assignmentId, config.dataKey, image.storageFileId);
      setStorageUrls(prev => ({ ...prev, [image.id]: blobUrl }));
    } catch {
      // Failed to load - will show placeholder
    }
  }, [assignmentId, config.dataKey, storageUrls]);

  useEffect(() => {
    images.forEach(img => {
      if (img.imageType === 'storage' && img.storageFileId && !storageUrls[img.id]) {
        loadStorageImage(img);
      }
    });
  }, [images, loadStorageImage, storageUrls]);

  // Helper to get the display URL for an image
  const getImageSrc = (image: RepositoryImage): string => {
    if (image.imageType === 'storage') {
      return storageUrls[image.id] || '';
    }
    return image.imageData;
  };

  const selectedImage = selectedImageIndex !== null && sortedImages[selectedImageIndex]
    ? sortedImages[selectedImageIndex]
    : null;

  const processFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.warning('Please select an image file');
      return;
    }

    setUploadFile(file);

    // Read file as base64 for preview (and for non-storage fallback)
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setUploadPreview(base64);
      setUploadMimeType(file.type);
      setNewImageTitle(file.name.replace(/\.[^/.]+$/, '')); // Remove extension
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!uploadPreview || !newImageTitle.trim()) return;

    // Use storage API when assignmentId is available
    if (useStorageApi && uploadFile && assignmentId) {
      setIsUploading(true);
      try {
        const fileInfo = await uploadWidgetFile(assignmentId, config.dataKey, uploadFile);
        createItem({
          title: newImageTitle.trim(),
          description: newImageDescription.trim() || undefined,
          tags: [],
          imageData: '',
          imageType: 'storage',
          mimeType: uploadMimeType,
          storageFileId: fileInfo.id,
        } as Omit<RepositoryImage, 'id' | 'createdAt' | 'updatedAt'>);
        // Cache the preview so it displays immediately
        const newId = images.length > 0 ? `img-${Date.now()}` : '';
        if (uploadPreview && newId) {
          setStorageUrls(prev => ({ ...prev, [newId]: uploadPreview }));
        }
      } catch {
        toast.error('Failed to upload image');
      } finally {
        setIsUploading(false);
      }
    } else {
      // Fallback: store as base64 in dashboard data (no assignment context)
      createItem({
        title: newImageTitle.trim(),
        description: newImageDescription.trim() || undefined,
        tags: [],
        imageData: uploadPreview,
        imageType: 'base64',
        mimeType: uploadMimeType,
      } as Omit<RepositoryImage, 'id' | 'createdAt' | 'updatedAt'>);
    }
    resetUploadModal();
  };

  const resetUploadModal = () => {
    setShowUploadModal(false);
    setUploadPreview(null);
    setUploadFile(null);
    setNewImageTitle('');
    setNewImageDescription('');
    setUploadMimeType('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = (imageId: string) => {
    if (!onDataChange) return;
    deleteItem(imageId);
  };

  const confirmDeleteImage = async () => {
    // Soft-delete from storage if it's a storage-type image
    if (deletingImageId && assignmentId) {
      const image = images.find(img => img.id === deletingImageId);
      if (image?.imageType === 'storage' && image.storageFileId) {
        try {
          await deleteWidgetFile(assignmentId, config.dataKey, image.storageFileId);
        } catch {
          // Storage delete failed - still remove from local data
        }
      }
    }
    confirmDelete();
    setSelectedImageIndex(null);
  };

  const handleEditSave = () => {
    if (!editingImage) return;
    updateItem(editingImage.id, {
      title: newImageTitle.trim() || editingImage.title,
      description: newImageDescription.trim() || undefined,
    } as Partial<RepositoryImage>);
    setShowEditModal(false);
    setEditingImage(null);
  };

  const openInNewTab = (image: RepositoryImage) => {
    if (image.imageType === 'storage' && image.storageFileId && assignmentId) {
      window.open(`/viewer/${assignmentId}/${config.dataKey}/${image.storageFileId}`, '_blank');
    } else if (image.imageType === 'base64' && image.imageData) {
      // Base64: open blob in new tab
      const win = window.open('', '_blank');
      if (win) {
        win.document.title = image.title;
        win.document.body.style.margin = '0';
        win.document.body.style.background = '#111';
        win.document.body.style.display = 'flex';
        win.document.body.style.alignItems = 'center';
        win.document.body.style.justifyContent = 'center';
        win.document.body.style.height = '100vh';
        const img = win.document.createElement('img');
        img.src = image.imageData;
        img.style.maxWidth = '100%';
        img.style.maxHeight = '100%';
        img.style.objectFit = 'contain';
        win.document.body.appendChild(img);
      }
    } else {
      // Fallback to in-widget popout
      setIsFullscreen(true);
    }
  };

  const openEditModal = (image: RepositoryImage) => {
    setEditingImage(image);
    setNewImageTitle(image.title);
    setNewImageDescription(image.description || '');
    setShowEditModal(true);
  };

  const navigateImage = (direction: 'prev' | 'next') => {
    if (selectedImageIndex === null) return;
    const newIndex = direction === 'prev'
      ? Math.max(0, selectedImageIndex - 1)
      : Math.min(sortedImages.length - 1, selectedImageIndex + 1);
    setSelectedImageIndex(newIndex);
  };

  const getThumbnailSize = () => {
    switch (config.thumbnailSize) {
      case 'small': return 'w-24 h-24';
      case 'large': return 'w-48 h-48';
      default: return 'w-32 h-32';
    }
  };

  // Reusable SVG icons
  const PlusIcon = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );

  const ChevronLeftIcon = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  );

  const ExpandIcon = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
    </svg>
  );

  const ShrinkIcon = (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
    </svg>
  );

  const EditIcon = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );

  const DeleteIcon = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );

  const CloseIcon = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  const LinkIcon = (
    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  );

  const CheckIcon = (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );

  const CopyRefIcon = (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  );

  // No data key configured
  if (!config.dataKey) {
    return (
      <WidgetEmptyState
        icon={<span className="text-2xl">{getWidgetIcon('image')}</span>}
        title="No data key configured"
        description="Configure a data key in the widget settings."
      />
    );
  }

  return (
    <div className="h-full flex flex-col bg-surface overflow-hidden relative">
      {/* Header */}
      <div className="px-4 py-3 border-b bg-neutral-50 flex items-center gap-3">
        {/* Search */}
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search..."
          className="flex-1"
        />

        {/* Count */}
        <p className="text-xs text-neutral-500 flex-shrink-0 ml-auto">
          {searchQuery
            ? `${filteredImages.length} of ${images.length} items`
            : `${images.length} items`
          }
        </p>

        {/* Sort */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'title' | 'created' | 'updated')}
          className="text-xs border border-neutral-200 rounded-lg px-2 h-9 bg-surface text-neutral-700 flex-shrink-0"
        >
          <option value="updated">Last Updated</option>
          <option value="created">Date Added</option>
          <option value="title">Title</option>
        </select>

        {/* View toggle */}
        <div className="flex border border-neutral-200 rounded-lg overflow-hidden flex-shrink-0 h-9">
          <button
            onClick={() => setShowList(true)}
            className={`px-2.5 flex items-center ${showList ? 'bg-neutral-200 text-neutral-700' : 'text-neutral-400 hover:text-neutral-600'}`}
            title="List view"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <button
            onClick={() => setShowList(false)}
            className={`px-2.5 flex items-center ${!showList ? 'bg-neutral-200 text-neutral-700' : 'text-neutral-400 hover:text-neutral-600'}`}
            title="Grid view"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
          </button>
        </div>

        {/* Upload Button */}
        {!readOnly && config.allowUpload !== false && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowUploadModal(true)}
            className="min-w-[90px] justify-center"
          >
            Upload
          </Button>
        )}
      </div>

      {/* Content */}
      <div className={`flex-1 overflow-auto ${showList ? '' : 'p-4'}`}>
        {sortedImages.length === 0 ? (
          <WidgetEmptyState
            icon={<FontAwesomeIcon icon={faCamera} />}
            title={searchQuery ? 'No images match your search' : 'No images yet'}
            description={
              searchQuery
                ? 'Try a different search term'
                : (!readOnly && config.allowUpload !== false
                    ? 'Click Upload to add your first image'
                    : 'No images have been added')
            }
            action={
              !searchQuery && !readOnly && config.allowUpload !== false ? (
                <Button variant="primary" size="sm" onClick={() => setShowUploadModal(true)} className="min-w-[90px] justify-center">
                  Upload
                </Button>
              ) : undefined
            }
          />
        ) : !showList ? (
          /* Grid View */
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {sortedImages.map((image, index) => (
              <div
                key={image.id}
                className={`group relative rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                  selectedImageIndex === index ? 'border-primary-500 ring-2 ring-primary-200' : 'border-neutral-200 hover:border-neutral-300'
                }`}
                onClick={() => openInNewTab(image)}
              >
                <div className={`${getThumbnailSize()} w-full aspect-square bg-neutral-100`}>
                  <LoadingImg src={getImageSrc(image)} alt={image.title} className="w-full h-full object-cover" />
                </div>
                {/* Pin button — top left */}
                <button
                  onClick={(e) => { e.stopPropagation(); updateItem(image.id, { isPinned: !image.isPinned } as Partial<RepositoryImage>); }}
                  className={`absolute top-1 left-1 p-1 rounded transition-all ${
                    image.isPinned ? 'text-warning-500 bg-black/70' : 'text-white bg-black/70 opacity-0 group-hover:opacity-100 hover:bg-black/90'
                  }`}
                  title={image.isPinned ? 'Unpin' : 'Pin to top'}
                >
                  <svg className="w-3.5 h-3.5" fill={image.isPinned ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" /></svg>
                </button>
                {/* Overlay buttons — top right */}
                <div className="absolute top-1 right-1 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-all">
                  <button
                    onClick={(e) => handleCopyRef(image.id, e)}
                    className={`p-1 rounded ${copiedId === image.id ? 'bg-success-500 text-white' : 'bg-black/70 text-white hover:bg-black/90'}`}
                    title="Copy link"
                  >
                    {copiedId === image.id ? CheckIcon : CopyRefIcon}
                  </button>
                  <a
                    href={getImageSrc(image)}
                    download={image.title}
                    onClick={(e) => e.stopPropagation()}
                    className="p-1 rounded bg-black/70 text-white hover:bg-black/90"
                    title="Download"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  </a>
                </div>
                <div className="p-2 bg-surface">
                  <p className="text-sm font-medium text-neutral-900 truncate">{image.title}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="divide-y divide-neutral-100">
            {sortedImages.map((image, index) => (
              <div
                key={image.id}
                className={`group flex items-center gap-3 h-[72px] px-4 cursor-pointer transition-all ${
                  selectedImageIndex === index ? 'bg-primary-50 border-l-4 border-l-primary-500' : 'hover:bg-neutral-50'
                }`}
                onClick={() => openInNewTab(image)}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); updateItem(image.id, { isPinned: !image.isPinned } as Partial<RepositoryImage>); }}
                  className={`p-1 rounded flex-shrink-0 transition-all ${
                    image.isPinned ? 'text-warning-500' : 'text-neutral-300 opacity-0 group-hover:opacity-100 hover:text-warning-500'
                  }`}
                  title={image.isPinned ? 'Unpin' : 'Pin to top'}
                >
                  <svg className="w-4 h-4" fill={image.isPinned ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" /></svg>
                </button>
                <div className="w-14 h-14 bg-neutral-100 rounded overflow-hidden flex-shrink-0">
                  <LoadingImg src={getImageSrc(image)} alt={image.title} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-neutral-900 truncate">{image.title}</p>
                  {image.description && (
                    <p className="text-sm text-neutral-500 truncate mt-0.5">{image.description}</p>
                  )}
                </div>
                <button
                  onClick={(e) => handleCopyRef(image.id, e)}
                  className={`p-1.5 rounded flex-shrink-0 transition-all ${
                    copiedId === image.id ? 'text-success-600' : 'text-neutral-400 opacity-0 group-hover:opacity-100 hover:text-neutral-600'
                  }`}
                  title={copiedId === image.id ? 'Copied!' : 'Copy link'}
                >
                  {copiedId === image.id
                    ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                  }
                </button>
                <a
                  href={getImageSrc(image)}
                  download={image.title}
                  onClick={(e) => e.stopPropagation()}
                  className="p-1.5 rounded flex-shrink-0 text-neutral-400 opacity-0 group-hover:opacity-100 hover:text-neutral-600 transition-all"
                  title="Download"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                </a>
                <p className="text-xs text-neutral-400 flex-shrink-0">
                  {new Date(image.createdAt).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Inline Image Viewer - Shows inside the widget panel */}
      {selectedImage && viewMode === 'inline' && !isFullscreen && (
        <div className="absolute inset-0 z-20 bg-surface flex flex-col">
          {/* Viewer Header */}
          <div className="px-3 py-2 border-b bg-neutral-50 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Button
                variant="ghost"
                size="xs"
                icon={ChevronLeftIcon}
                onClick={() => {
                  setViewMode('grid');
                  setSelectedImageIndex(null);
                }}
                title="Back to grid"
              />
              <div className="min-w-0 flex-1">
                <h3 className="font-medium text-neutral-900 text-sm truncate">{selectedImage.title}</h3>
                <div className="flex flex-wrap gap-1 items-center">
                  {/* Reference Link */}
                  <button
                    onClick={() => handleCopyRef(selectedImage.id)}
                    className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors ${
                      copiedId === selectedImage.id
                        ? 'bg-success-100 text-success-700'
                        : 'bg-neutral-100 text-neutral-600 hover:bg-primary-100 hover:text-primary-700'
                    }`}
                    title="Click to copy reference link"
                  >
                    {LinkIcon}
                    {copiedId === selectedImage.id ? 'Copied!' : selectedImage.id}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button
                variant="ghost"
                size="xs"
                icon={ExpandIcon}
                onClick={() => selectedImage && openInNewTab(selectedImage)}
                title="Open in new tab"
              />
              {!readOnly && config.allowTagging !== false && (
                <Button
                  variant="ghost"
                  size="xs"
                  icon={EditIcon}
                  onClick={() => openEditModal(selectedImage)}
                  title="Edit"
                />
              )}
              {!readOnly && config.allowDelete !== false && (
                <Button
                  variant="ghost"
                  size="xs"
                  icon={DeleteIcon}
                  onClick={() => handleDelete(selectedImage.id)}
                  title="Delete"
                  className="hover:text-danger-600 hover:bg-danger-50"
                />
              )}
              <Button
                variant="ghost"
                size="xs"
                icon={CloseIcon}
                onClick={() => {
                  setViewMode('grid');
                  setSelectedImageIndex(null);
                }}
                title="Close"
              />
            </div>
          </div>

          {/* Image - takes full available space */}
          <div className="flex-1 relative overflow-hidden bg-neutral-100 flex items-center justify-center p-2">
            <img
              src={getImageSrc(selectedImage)}
              alt={selectedImage.title}
              className="max-w-full max-h-full object-contain"
            />

            {/* Navigation Arrows */}
            {selectedImageIndex !== null && selectedImageIndex > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); navigateImage('prev'); }}
                className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-surface/90 rounded-full shadow-lg hover:bg-surface"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            {selectedImageIndex !== null && selectedImageIndex < sortedImages.length - 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); navigateImage('next'); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-surface/90 rounded-full shadow-lg hover:bg-surface"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>

          {/* Notes + Navigation footer */}
          <div className="border-t bg-neutral-50">
            {!readOnly && (
              <div className="px-3 py-2">
                <textarea
                  value={selectedImage.description || ''}
                  onChange={(e) => updateItem(selectedImage.id, { description: e.target.value || undefined } as Partial<RepositoryImage>)}
                  placeholder="Add notes..."
                  rows={2}
                  className="w-full px-2 py-1.5 text-xs border border-neutral-200 rounded-lg resize-none bg-surface placeholder-neutral-400"
                />
              </div>
            )}
            {readOnly && selectedImage.description && (
              <div className="px-3 py-2">
                <p className="text-xs text-neutral-600">{selectedImage.description}</p>
              </div>
            )}
            <div className="px-3 py-1.5 border-t flex items-center justify-between text-xs text-neutral-500">
              <span>{(selectedImageIndex ?? 0) + 1} of {sortedImages.length}</span>
            </div>
          </div>
        </div>
      )}

      {/* Popout Image Viewer */}
      <PopoutWindow
        isOpen={selectedImage !== null && isFullscreen}
        onClose={() => {
          setIsFullscreen(false);
          setViewMode('grid');
          setSelectedImageIndex(null);
        }}
        title={selectedImage?.title || 'Image Viewer'}
        headerActions={
          selectedImage ? (
            <>
              {/* Reference Link */}
              <button
                onClick={() => handleCopyRef(selectedImage.id)}
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors ${
                  copiedId === selectedImage.id
                    ? 'bg-success-100 text-success-700'
                    : 'bg-neutral-100 text-neutral-500 hover:bg-primary-100 hover:text-primary-700'
                }`}
                title="Copy reference link"
              >
                {LinkIcon}
                {copiedId === selectedImage.id ? 'Copied!' : 'ref'}
              </button>
              {!readOnly && config.allowTagging !== false && (
                <button onClick={() => openEditModal(selectedImage)} className="p-1 rounded hover:bg-neutral-200 text-neutral-500 hover:text-neutral-700 [&_svg]:w-3.5 [&_svg]:h-3.5" title="Edit title">
                  {EditIcon}
                </button>
              )}
              {!readOnly && config.allowDelete !== false && (
                <button onClick={() => handleDelete(selectedImage.id)} className="p-1 rounded hover:bg-danger-100 text-neutral-500 hover:text-danger-600 [&_svg]:w-3.5 [&_svg]:h-3.5" title="Delete">
                  {DeleteIcon}
                </button>
              )}
            </>
          ) : undefined
        }
      >
        {selectedImage && (
          <div className="h-full flex flex-col">
            {/* Image */}
            <div className="flex-1 relative overflow-auto bg-neutral-100 flex items-center justify-center p-4">
              <img
                src={getImageSrc(selectedImage)}
                alt={selectedImage.title}
                className="max-w-full max-h-full object-contain"
              />

              {/* Navigation Arrows */}
              {selectedImageIndex !== null && selectedImageIndex > 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); navigateImage('prev'); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-surface rounded-full shadow-lg hover:bg-neutral-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              {selectedImageIndex !== null && selectedImageIndex < sortedImages.length - 1 && (
                <button
                  onClick={(e) => { e.stopPropagation(); navigateImage('next'); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-surface rounded-full shadow-lg hover:bg-neutral-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>

            {/* Notes + Navigation footer */}
            <div className="border-t bg-neutral-50 flex-shrink-0">
              {!readOnly && (
                <div className="px-4 py-2">
                  <textarea
                    value={selectedImage.description || ''}
                    onChange={(e) => updateItem(selectedImage.id, { description: e.target.value || undefined } as Partial<RepositoryImage>)}
                    placeholder="Add notes about this image..."
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg resize-none bg-surface placeholder-neutral-400"
                  />
                </div>
              )}
              {readOnly && selectedImage.description && (
                <div className="px-4 py-2">
                  <p className="text-sm text-neutral-600">{selectedImage.description}</p>
                </div>
              )}
              <div className="px-4 py-1.5 border-t flex items-center justify-between text-xs text-neutral-500">
                <span>{(selectedImageIndex ?? 0) + 1} of {sortedImages.length}</span>
              </div>
            </div>
          </div>
        )}
      </PopoutWindow>

      {/* Upload Modal */}
      <RepositoryModal
        isOpen={showUploadModal}
        onClose={resetUploadModal}
        title="Upload Image"
        onSave={handleUpload}
        saveLabel={isUploading ? 'Uploading...' : 'Upload'}
        saveDisabled={!uploadPreview || !newImageTitle.trim() || isUploading}
        hideFields={!uploadPreview}
        itemTitle={newImageTitle}
        onTitleChange={setNewImageTitle}
        description={newImageDescription}
        onDescriptionChange={setNewImageDescription}
        topChildren={
          uploadPreview ? (
            <div className="border rounded-lg p-3 bg-neutral-50 relative">
              <img src={uploadPreview} alt="Preview" className="max-h-48 mx-auto rounded" />
              <button
                onClick={() => { setUploadPreview(null); setUploadFile(null); setNewImageTitle(''); }}
                className="absolute top-2 right-2 p-1 bg-black/50 text-white rounded-full hover:bg-black/70"
                title="Remove"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <DropZone accept="image/*" onFile={processFile} label="Drop image here or click to browse" hint="PNG, JPG, GIF, SVG" />
          )
        }
      />

      {/* Edit Modal */}
      <RepositoryModal
        isOpen={showEditModal && editingImage !== null}
        onClose={() => setShowEditModal(false)}
        title="Edit Image"
        onSave={handleEditSave}
        saveLabel="Save Changes"
        itemTitle={newImageTitle}
        onTitleChange={setNewImageTitle}
        description={newImageDescription}
        onDescriptionChange={setNewImageDescription}
        topChildren={
          editingImage ? (
            <div className="border rounded-lg p-3 bg-neutral-50">
              <img
                src={getImageSrc(editingImage)}
                alt="Preview"
                className="max-h-32 mx-auto rounded"
              />
            </div>
          ) : undefined
        }
      />

      <ConfirmationModal
        isOpen={deletingImageId !== null}
        onClose={() => setDeletingImageId(null)}
        onAction={confirmDeleteImage}
        title="Delete Image"
        description="Are you sure you want to delete this image?"
        actionText="Delete"
      />
    </div>
  );
};

export default ImageRepositoryWidgetRenderer;
